import React from "react";
import { Badge, Button, Callout, Card, DataTable, Dialog, Field, Input, PageHeader, Select, Tabs } from "../components/DesignSystem";
import type { DocumentItem, ID } from "../domain/types";
import { errorMessage } from "../services/errors";
import type { RouteKey } from "../shell/PlannerShell";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useSubmitGuard } from "./common";

const MAX_DOCUMENT_SIZE_BYTES = 25 * 1024 * 1024;
const ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
];

export function UpdatesScreen({ go }: { go: (route: RouteKey) => void }) {
  const { workspace, loading, error, service, run } = usePlannerData();
  const [tab, setTab] = React.useState<"notifications" | "activity">("notifications");
  const [kind, setKind] = React.useState("all");
  const [actionError, setActionError] = React.useState<string | null>(null);
  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading updates" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;
  const rows = workspace.notifications.filter((item) => kind === "all" || item.kind.toLowerCase() === kind);
  const unread = workspace.notifications.filter((item) => !item.read).length;
  const markAllRead = async () => {
    setActionError(null);
    try {
      await run(() => service.notifications.markAllRead());
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };
  const openNotification = async (row: typeof rows[number]) => {
    setActionError(null);
    try {
      await run(() => service.notifications.markRead(row.id));
      go(row.route as RouteKey);
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };
  return (
    <div>
      <PageHeader
        eyebrow="Account"
        title="Updates"
        meta={tab === "notifications" ? [{ icon: "bell", label: `${unread} unread` }] : [{ icon: "history", label: "Activity log" }]}
        actions={tab === "notifications" ? <Button disabled={unread === 0} onClick={markAllRead}>Mark all read</Button> : undefined}
        tabs={<Tabs value={tab} onChange={setTab} tabs={[{ value: "notifications", label: "Notifications" }, { value: "activity", label: "Activity" }]} />}
      />
      <div className="pob-pad">
        {actionError && <Callout tone="error" title="Update action failed">{actionError}</Callout>}
        {tab === "notifications" ? <>
          <Tabs value={kind} onChange={setKind} tabs={[{ value: "all", label: "All" }, { value: "rsvp", label: "RSVP" }, { value: "payment", label: "Payments" }, { value: "task", label: "Tasks" }, { value: "logistics", label: "Logistics" }]} />
          <Card padding="0">
            <DataTable rows={rows} onRowClick={(row) => { void openNotification(row); }} columns={[
              { key: "when", header: "When", width: 120, muted: true },
              { key: "kind", header: "Kind", width: 110, render: (row) => <Badge tone={row.kind === "Payment" ? "error" : row.kind === "RSVP" ? "success" : "neutral"}>{row.kind}</Badge> },
              { key: "text", header: "Notice", wrap: true, render: (row) => <span style={{ font: row.read ? "var(--type-body)" : "var(--type-label)" }}>{row.text}</span> },
              { key: "x", header: "", width: 90, render: (row) => <Button size="sm">{row.action}</Button> }
            ]} />
          </Card>
        </> : (
          <Card eyebrow="Change history" title="What has happened" padding="0">
            <DataTable rows={workspace.activity} columns={[
              { key: "when", header: "When", width: 130, muted: true },
              { key: "who", header: "Who", width: 90 },
              { key: "text", header: "Change", wrap: true }
            ]} />
          </Card>
        )}
      </div>
    </div>
  );
}

function formatStorageBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

export function DocumentsScreen() {
  const { workspace, loading, error, service, run } = usePlannerData();
  const [uploading, setUploading] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<ID | null>(null);
  const [usage, setUsage] = React.useState<{ usedBytes: number; limitBytes: number } | null>(null);
  const { submitting: uploadingFile, guardedSubmit: guardedUpload } = useSubmitGuard();

  React.useEffect(() => {
    let cancelled = false;
    service.documents.getStorageUsage().then((result) => { if (!cancelled) setUsage(result); }).catch(() => {});
    return () => { cancelled = true; };
  }, [service, workspace?.documents.length]);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading documents" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const usageRatio = usage ? usage.usedBytes / usage.limitBytes : 0;

  const download = async (document: DocumentItem) => {
    setActionError(null);
    setBusyId(document.id);
    try {
      const url = await service.documents.getDownloadUrl(document.id);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (document: DocumentItem) => {
    setActionError(null);
    setBusyId(document.id);
    try {
      await run(() => service.documents.deleteDocument(document.id));
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const upload = (file: File, input: { type: DocumentItem["type"]; linkedRecordLabel?: string; eventId?: string | "shared" }) => guardedUpload(async () => {
    setActionError(null);
    try {
      await run(() => service.documents.uploadDocument(file, input));
      setUploading(false);
    } catch (err) {
      setActionError(errorMessage(err));
    }
  });

  return (
    <div>
      <PageHeader eyebrow="More" title="Documents" meta={[{ icon: "folder", label: `${workspace.documents.length} files` }]} actions={<Button variant="primary" icon="upload" onClick={() => setUploading(true)}>Upload</Button>} />
      <div className="pob-pad">
        {actionError && <Callout tone="warning" title="Document action failed">{actionError}</Callout>}
        {usage && usageRatio >= 0.8 && (
          <Callout tone={usageRatio >= 1 ? "error" : "warning"} title="Storage allowance">
            {formatStorageBytes(usage.usedBytes)} of {formatStorageBytes(usage.limitBytes)} used. {usageRatio >= 1 ? "New uploads are paused until you free up space or upgrade — existing files are unaffected." : "Consider removing unused files or upgrading your plan soon."}
          </Callout>
        )}
        <Card padding="0">
          <DataTable
            rows={workspace.documents}
            emptyLabel="No documents yet"
            columns={[
              { key: "name", header: "File", render: (row) => <span style={{ font: "var(--type-label)" }}>{row.name}</span> },
              { key: "type", header: "Type", render: (row) => <Badge tone="neutral">{row.type}</Badge> },
              { key: "linkedRecordLabel", header: "Linked to", muted: true },
              { key: "size", header: "Size", muted: true },
              { key: "addedAt", header: "Added", muted: true },
              { key: "uploadedBy", header: "By", muted: true },
              { key: "actions", header: "", width: 170, render: (row) => (
                <span style={{ display: "flex", gap: "var(--space-2)" }}>
                  <Button size="sm" disabled={!row.hasFile || busyId === row.id} onClick={() => download(row)}>{busyId === row.id ? "…" : "Download"}</Button>
                  <Button size="sm" variant="danger" disabled={busyId === row.id} onClick={() => remove(row)}>Delete</Button>
                </span>
              ) }
            ]}
          />
        </Card>
      </div>
      {uploading && <UploadDialog submitting={uploadingFile} onClose={() => setUploading(false)} onSubmit={upload} />}
    </div>
  );
}

function UploadDialog({ onClose, onSubmit, submitting }: { onClose: () => void; onSubmit: (file: File, input: { type: DocumentItem["type"]; linkedRecordLabel?: string; eventId?: string | "shared" }) => void; submitting?: boolean }) {
  const [file, setFile] = React.useState<File | null>(null);
  const [type, setType] = React.useState<DocumentItem["type"]>("Contract");
  const [linkedRecordLabel, setLinkedRecordLabel] = React.useState("");
  const [fileError, setFileError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFile = (files: FileList | null) => {
    const selected = files?.[0];
    if (!selected) return;
    if (!ALLOWED_DOCUMENT_TYPES.includes(selected.type)) {
      setFileError("That file type isn't supported. Use a PDF, Word/Excel document, or an image.");
      setFile(null);
      return;
    }
    if (selected.size > MAX_DOCUMENT_SIZE_BYTES) {
      setFileError("That file is larger than the 25 MB limit.");
      setFile(null);
      return;
    }
    setFileError(null);
    setFile(selected);
  };

  const valid = Boolean(file) && !fileError;

  return (
    <Dialog
      title="Upload document"
      description="Files are private to this wedding, stored securely, and never publicly listed."
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" disabled={!valid || submitting} onClick={() => file && onSubmit(file, { type, linkedRecordLabel: linkedRecordLabel || undefined })}>{submitting ? "Uploading…" : "Upload"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {fileError && <Callout tone="error" title="Can't use this file">{fileError}</Callout>}
        <div onClick={() => inputRef.current?.click()} style={{ border: "2px dashed var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "var(--space-6)", textAlign: "center", cursor: "pointer" }}>
          <input ref={inputRef} type="file" accept={ALLOWED_DOCUMENT_TYPES.join(",")} style={{ display: "none" }} onChange={(event) => handleFile(event.target.files)} />
          <p style={{ font: "var(--type-label)", margin: 0 }}>{file ? file.name : "Click to choose a file"}</p>
          <p style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)", marginTop: 4 }}>PDF, Word, Excel, or an image — up to 25 MB.</p>
        </div>
        <Field label="Type"><Select value={type} onChange={(event) => setType(event.target.value as DocumentItem["type"])} options={["Contract", "Invoice", "Receipt", "Quote", "Legal", "Other"]} /></Field>
        <Field label="Linked record (optional)"><Input value={linkedRecordLabel} onChange={(event) => setLinkedRecordLabel(event.target.value)} placeholder="Vendor, venue or payment" /></Field>
      </div>
    </Dialog>
  );
}
